import React, { useState, useRef, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Stack,
  Typography,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import { enqueueSnackbar } from "notistack";
import { useApiClient } from "../../../api";
import { FruitifyContainer } from "../../../shared-components/Containers";
import FruitifyIcon from "../../../shared-components/FruitifyIcons";
import GeneralLoading from "../../../shared-components/Loading";
import { IdentifierAutoComplete } from "../components/IdentifierAutoComplete";
import { ConfirmUploadDialog } from "../components/ConfirmUploadDialog";
import { withPageTracking } from "../../../services/postHog";

export const AssignmentUploadPage = () => {
  const { assignmentId } = useParams();
  const [searchParams] = useSearchParams();
  const user = searchParams.get("user");
  const token = searchParams.get("token");
  const [answerType, setAnswerType] = useState("handwritten");
  const ApiClient = useApiClient();

  const [dragging, setDragging] = useState(false);
  const [files, setFiles] = useState([]);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [tokenIsValid, setTokenIsValid] = useState(false);
  const [tokenErrorMessage, setTokenErrorMessage] = useState("");
  const [checkingToken, setCheckingToken] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [identifiers, setIdentifiers] = useState([]);
  const [identifier, setIdentifier] = useState({});
  const [identifierWarningMessage, setIdentifierWarningMessage] = useState(""); // eslint-disable-line
  const [isIndependentUser, setIsIndependentUser] = useState(false);

  const cameraInputRef = useRef(null);

  useEffect(() => {
    if (user && token) {
      verifyUploadToken();
    }
  }, [user, token]);

  const verifyUploadToken = async () => {
    try {
      const response = await ApiClient.assignment.verifyUploadToken({
        user: user,
        assignment: assignmentId,
        token: token,
      });
      if (response.success) {
        setTokenIsValid(response.isValid);
        if (response.isValid) {
          setIdentifiers(response.students);
        } else {
          setTokenErrorMessage(response.message);
        }
      } else {
        setTokenIsValid(false);
        setTokenErrorMessage(
          response.message || "Invalid upload URL or credentials",
        );
      }
      setIsIndependentUser(response.isIndependentUser);
    } catch (error) {
      console.error("Token validation failed", error);
      setTokenIsValid(false);
      setTokenErrorMessage("Unable to verify upload link. Please try again.");
    } finally {
      setCheckingToken(false);
    }
  };

  const onDropFiles = (event) => {
    event.preventDefault();
    setDragging(false);
    const incoming = Array.from(event.dataTransfer.files || []);
    const filtered = incoming.filter((f) => {
      const name = f.name.toLowerCase();
      return (
        name.endsWith(".pdf") || name.endsWith(".docx") || name.endsWith(".doc")
      );
    });
    if (filtered.length !== incoming.length) {
      enqueueSnackbar("Some files were ignored (only PDF/DOC/DOCX allowed)", {
        variant: "warning",
        autoHideDuration: 2500,
      });
    }
    setFiles((prev) => [...prev, ...filtered]);
  };

  const onSelectFiles = (event) => {
    const incoming = Array.from(event.target.files || []);
    const filtered = incoming.filter((f) => {
      const name = f.name.toLowerCase();
      return (
        name.endsWith(".pdf") || name.endsWith(".docx") || name.endsWith(".doc")
      );
    });
    if (filtered.length === 0) return;
    setFiles((prev) => [...prev, ...filtered]);
  };
  const handleChangeAnswerType = (event, newValue) => {
    if (newValue !== null) {
      setAnswerType(newValue);
    }
  };
  const handleSelectImages = (e) => {
    if (e.target.files) {
      const selected = Array.from(e.target.files);
      setImages((prev) => [...prev, ...selected]);
    }
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const openCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleConfirmSubmitImages = async () => {
    setSubmitting(true);
    try {
      const response = await ApiClient.assignment.submitUploadedImages({
        user: user,
        assignment: assignmentId,
        token: token,
        images: images,
        student: identifier.id,
      });
      if (response.success) {
        enqueueSnackbar("Successfully submitted, submit another?", {
          variant: "success",
          autoHideDuration: 3000,
        });
        setIdentifiers(response.students);
        setImages([]);
        setIdentifier({});
        setDialogOpen(false);
      } else {
        enqueueSnackbar(`${response.message}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar("Error submitting, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitFiles = async () => {
    if (
      !assignmentId ||
      files.length === 0 ||
      Object.keys(identifier).length === 0
    )
      return;
    setSubmitting(true);
    try {
      const res = await ApiClient.assignment.submitAssignmentFiles({
        assignment: assignmentId,
        files,
        user,
        token,
        student: identifier.id,
      });
      if (res?.success) {
        enqueueSnackbar("Files submitted for upload", { variant: "success" });
        if (res.students) {
          setIdentifiers(res.students);
        }
        setFiles([]);
        setIdentifier({});
      } else {
        enqueueSnackbar(res?.message || "Upload failed", { variant: "error" });
      }
    } catch (e) {
      enqueueSnackbar("Error submitting files", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingToken) {
    return <GeneralLoading />;
  }

  if (!tokenIsValid) {
    return (
      <FruitifyContainer p={2}>
        <Typography variant="h6" gutterBottom>
          {tokenErrorMessage || "Invalid upload URL or credentials"}
        </Typography>
      </FruitifyContainer>
    );
  }

  const submitDisabled = Object.keys(identifier).length === 0 || submitting;

  // Handwritten mode - image upload
  return (
    <FruitifyContainer p={2}>
      <ConfirmUploadDialog
        open={dialogOpen}
        setOpen={setDialogOpen}
        onConfirm={handleConfirmSubmitImages}
        submitting={submitting}
      />
      <ToggleButtonGroup
        value={answerType}
        exclusive
        onChange={handleChangeAnswerType}
        fullWidth
        size="small"
        sx={{ mt: 4 }}
      >
        <ToggleButton value="handwritten">Handwritten</ToggleButton>
        <ToggleButton value="document">Document</ToggleButton>
      </ToggleButtonGroup>
      <IdentifierAutoComplete
        availableIdentifiers={identifiers}
        setIdentifier={setIdentifier}
        identifier={identifier}
        identifierWarningMessage={identifierWarningMessage}
        isIndependentUser={isIndependentUser}
      />
      {answerType === "handwritten" && (
        <>
          <Typography variant="h6" gutterBottom>
            Upload handwritten answers (photos)
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <Button
                variant="contained"
                startIcon={<CameraAltIcon />}
                onClick={openCamera}
                fullWidth
              >
                Take Photo
              </Button>
              <Button variant="outlined" component="label" fullWidth>
                Choose Images
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleSelectImages}
                />
              </Button>
            </Stack>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: "none" }}
              onChange={handleSelectImages}
            />
            {images.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected images: {images.length}
                </Typography>
                <Stack spacing={1}>
                  {images.map((img, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="body2" noWrap>
                        {img.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveImage(idx)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
            <Button
              fullWidth
              variant="outlined"
              sx={{ mt: 3 }}
              onClick={() => setDialogOpen(true)}
              disabled={submitDisabled || images.length === 0}
            >
              Submit
            </Button>
          </Box>
        </>
      )}
      {answerType === "document" && (
        <>
          <Typography variant="h5" gutterBottom>
            Upload Assignment Files
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Drop student's answers as PDF or Word documents.
          </Typography>
          <Box
            onDrop={onDropFiles}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            sx={{
              border: "2px dashed",
              borderColor: dragging ? "divider" : "primary.main",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: dragging ? "action.hover" : "transparent",
              transition: "background-color 0.2s",
              mb: 3,
            }}
          >
            <Stack spacing={2} alignItems="center">
              <Typography variant="subtitle1" color="text.secondary">
                Choose files or drag and drop here
              </Typography>
              <Typography variant="body2" color="text.secondary">
                PDF or doc/docx formats
              </Typography>
              <Button
                variant="contained"
                component="label"
                sx={{ textTransform: "none" }}
              >
                Choose Files
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".pdf,.doc,.docx"
                  onChange={onSelectFiles}
                />
              </Button>
              {files.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Selected: {files.length} file(s)
                </Typography>
              )}
            </Stack>
          </Box>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<FruitifyIcon name={"Publish"} />}
            sx={{ textTransform: "none", px: 3, py: 1.5 }}
            onClick={handleSubmitFiles}
            disabled={
              files.length === 0 ||
              Object.keys(identifier).length === 0 ||
              submitting
            }
          >
            Submit
          </Button>
        </>
      )}
    </FruitifyContainer>
  );
};

export default withPageTracking(AssignmentUploadPage, {
  feature: "assignment-upload",
});
